import { useQueryClient } from "@tanstack/react-query";
import { useQueryStates } from "nuqs";
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { PageLayout } from "@transfr-inc/dashboard-components/layouts";

import {
  Loader,
  NotificationType,
  Tab,
  TabHeaders,
  Tabs,
} from "@transfr-inc/dashboard-components";

import {
  RecordStatus,
  RecordStatusDropdown,
} from "@components/custom/dropdowns";
import { ProductTabHeader } from "@components/custom/tabs/product-tab-header";
import { AddTraineeClassroomModal } from "@components/modals";
import { ShortcutAction } from "@components/shortcuts";
import { useNotificationContext } from "@contexts/notification-context";
import { useApiRequest } from "@lib/hooks";
import { traineeDetailParams } from "@lib/utils/search-params-parsers";
import {
  useArchiveClassroomVisible,
  useCurrentUser,
  useFeatures,
  useRole,
} from "@queries";
import { useContainer } from "@src/contexts/container-context";

import {
  trackLearnersArchived,
  trackLearnersReactivated,
} from "@lib/event-trackers";
import { getLearnerTerm } from "@transfr-inc/dashboard-components/constants";
import { TraineeClassroomTab } from "./components/trainee.classroom.tab";
import { TraineeHeader } from "./components/trainee.header";
import { TraineeToolbar } from "./components/trainee.toolbar";

import "./index.scss";

export default () => {
  const { userId } = useParams();
  const [
    { from: breadcrumbLabel, classroomId, product, path },
    setTraineeParams,
  ] = useQueryStates(traineeDetailParams);

  const [isLoading, setIsLoading] = useState(true);
  const [trainee, setTrainee] = useState();
  const [classrooms, setClassrooms] = useState();
  const [statusFilter, setStatusFilter] = useState(RecordStatus.Active);
  const archivedFallbackAttempted = useRef(false);
  const [openAddToClassroom, setOpenAddToClassroom] = useState();
  const { addNotification } = useNotificationContext();
  const { userService, organizationService, classroomService } = useContainer();
  const features = useFeatures();
  const { canEditSTUDENTUser: editEnabled } = features?.editableUsers || {};
  const archiveClassroomVisible = useArchiveClassroomVisible();
  const queryClient = useQueryClient();
  const role = useRole();
  const { userId: currentUserId, organizationCode } = useCurrentUser() || {};

  const onArchiveStatusChange = () => {
    queryClient.invalidateQueries({
      queryKey: ["organizationUsers", role, organizationCode, currentUserId],
    });
    getTrainee();
  };

  const onArchivedHandler = () => {
    trackLearnersArchived([trainee], {
      source: "detail",
      batchId: crypto.randomUUID(),
    });
    onArchiveStatusChange();
  };

  const onUnarchivedHandler = () => {
    trackLearnersReactivated([trainee], { batchId: crypto.randomUUID() });
    onArchiveStatusChange();
  };

  const { loading: loadingTrainee, sendRequest: getTrainee } = useApiRequest(
    () =>
      userService
        .getUserDetails(userId, { status: RecordStatus.All })
        .then(setTrainee)
  );

  const { response: organization, sendRequest: getOrgDetails } = useApiRequest(
    () => organizationService.getOrganizationDetails(trainee?.organizationCode),
    false
  );

  const sortByArchived = (list) =>
    [...list].sort((a, b) => Number(!!a.isArchived) - Number(!!b.isArchived));

  const { loading: loadingClassrooms, sendRequest: getClassrooms } =
    useApiRequest(
      () =>
        userService
          .getUserClassrooms(userId, statusFilter)
          .then((results) => setClassrooms(sortByArchived(results))),
      false
    );

  useEffect(() => {
    getClassrooms();
  }, [statusFilter]);

  useEffect(() => {
    if (
      !classrooms ||
      !classroomId ||
      archivedFallbackAttempted.current ||
      statusFilter === RecordStatus.All
    ) {
      return;
    }

    const found = classrooms.some((c) => c.classroomId === classroomId);
    if (!found) {
      archivedFallbackAttempted.current = true;
      setStatusFilter(RecordStatus.All);
    }
  }, [classrooms, classroomId, statusFilter]);

  const onEditTrainee = async (updatedTrainee) => {
    try {
      const updatedUser = await userService.updateUserMetadata({
        userId,
        ...updatedTrainee,
      });
      setTrainee(updatedUser);
      const { firstName, lastName } = updatedTrainee;
      showSuccessNotification(
        <>
          The details for <strong>{`${firstName} ${lastName}`.trim()}</strong>
          &nbsp;have been updated.
        </>
      );
      return updatedUser;
    } catch (error) {
      console.error(error);
      const { data, status } = error;
      updatedTrainee.error =
        data?.detail ?? "Something went wrong, please try again later.";
      updatedTrainee.status = status;
      return updatedTrainee;
    }
  };

  const onRemoveTraineeFromClassroom = (trainee, classroom) => {
    const { classroomId, name } = classroom;
    return classroomService
      .removeTrainee(classroomId, trainee.userId)
      .then(() => {
        setClassrooms(classrooms.filter((c) => c.classroomId !== classroomId));
        showSuccessNotification(
          <>
            {getLearnerTerm()} has been removed from classroom{" "}
            <strong>{name}</strong>
          </>
        );
      });
  };

  const onAddTraineeToClassroom = (classroom) => {
    const { classroomId, name } = classroom;
    const { userId, firstName, lastName } = trainee;

    return classroomService.addTrainee(classroomId, userId).then(() => {
      setOpenAddToClassroom();
      showSuccessNotification(
        <>
          <strong>{`${firstName} ${lastName}`}</strong> has been added to
          classroom <strong>{name}</strong>
        </>
      );
      setClassrooms(sortByArchived([...classrooms, classroom]));
    });
  };

  const onPasswordUpdated = () => {
    showSuccessNotification("Password successfully changed.");
  };

  const showSuccessNotification = (message) => {
    addNotification({
      type: NotificationType.success,
      message,
      icon: ["fa-solid", "circle-check"],
    });
  };

  useEffect(() => {
    !loadingTrainee && getOrgDetails();
  }, [loadingTrainee]);

  useEffect(() => {
    setIsLoading(loadingClassrooms || loadingTrainee || !organization);
  }, [loadingClassrooms, loadingTrainee, organization]);

  const setTabState = (tabName) =>
    setTraineeParams({ classroomId: tabName, product: null });

  const getSelectedTab = () => {
    const findFn = classroomId
      ? (c) => c.classroomId === classroomId
      : (c) => c.products.includes(product);
    return classroomId || product
      ? classrooms.find((c) => findFn(c))?.classroomId
      : classrooms[0]?.classroomId;
  };

  const pageHeader = (
    <TraineeHeader
      organization={organization}
      trainee={trainee}
      breadcrumbLabel={breadcrumbLabel}
      path={path}
    >
      <TraineeToolbar
        trainee={trainee}
        onEditTrainee={onEditTrainee}
        onPasswordUpdated={onPasswordUpdated}
        onArchived={onArchivedHandler}
        onUnarchived={onUnarchivedHandler}
        editEnabled={editEnabled}
      ></TraineeToolbar>
    </TraineeHeader>
  );

  return !isLoading ? (
    <PageLayout className={"trainee-detail-page"} header={pageHeader}>
      <div className="trainee-detail-content">
        <div className="trainee-classrooms-header">
          <h2>
            Classrooms{" "}
            <span className="badge">
              {isLoading ? "..." : classrooms.length}
            </span>
          </h2>
          {archiveClassroomVisible && (
            <RecordStatusDropdown
              className="classroom-status-dropdown"
              selected={statusFilter}
              onChange={setStatusFilter}
            />
          )}
        </div>
        {classrooms?.length > 0 ? (
          <Tabs
            className="trainee-detail-tabs"
            selectedTabName={isLoading ? "" : getSelectedTab()}
          >
            <TabHeaders className="classroom-tabs">
              {classrooms?.map((c) => (
                <ProductTabHeader
                  onTabClick={setTabState}
                  key={c.classroomId}
                  name={c.classroomId}
                  productId={c.products[0]}
                  text={c.name}
                  isArchived={c.isArchived}
                />
              ))}
            </TabHeaders>
            {classrooms?.map((classroom) => (
              <Tab
                key={classroom.classroomId}
                name={classroom.classroomId}
                className={"classroom-tab"}
              >
                <TraineeClassroomTab
                  trainee={trainee}
                  classroom={classroom}
                  organization={organization}
                  onRemoveTrainee={onRemoveTraineeFromClassroom}
                  removeTraineeEnabled={editEnabled && classrooms?.length > 1}
                />
              </Tab>
            ))}
          </Tabs>
        ) : (
          <div className="no-classrooms-container">
            <ShortcutAction
              icon={["fa-light", "screen-users"]}
              onClick={() => setOpenAddToClassroom(true)}
              disabled={!editEnabled}
            >
              {editEnabled
                ? `Add ${getLearnerTerm()} to a Classroom`
                : `This ${getLearnerTerm({
                    lowercase: true,
                  })} is not enrolled in a classroom.`}
            </ShortcutAction>
          </div>
        )}
      </div>
      <AddTraineeClassroomModal
        open={openAddToClassroom}
        orgCode={trainee?.organizationCode}
        onCancel={() => setOpenAddToClassroom()}
        onAddTrainee={onAddTraineeToClassroom}
        async
      ></AddTraineeClassroomModal>
    </PageLayout>
  ) : (
    <Loader overlay fixed />
  );
};
